import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';

const ConfirmationModal = ({ show, title, message, onConfirm, onCancel, isLoading }) => {
  const { isDarkMode } = useDarkMode();
  const modalRef = useRef(null);

  // Handle outside click to close modal
  const handleOverlayClick = (e) => {
    // Only close if clicking the overlay itself, not the modal content
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-[2px]"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
      onClick={handleOverlayClick}
    >
      {/* Modal content */}
      <div
        ref={modalRef}
        className="relative w-full max-w-md rounded-xl shadow-2xl"
        style={{
          backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
          border: isDarkMode ? '1px solid rgba(71, 85, 105, 0.4)' : '1px solid #e2e8f0'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 space-y-4">
          <h3 
            className="text-xl font-semibold"
            style={{ color: isDarkMode ? '#ffffff' : '#0f172a' }}
          >
            {title}
          </h3>
          <p 
            className="text-sm leading-relaxed"
            style={{ color: isDarkMode ? '#e2e8f0' : '#475569' }}
          >
            {message}
          </p>
          
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              className="px-5 py-2.5 rounded-lg font-medium text-sm focus:outline-none focus:ring-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                color: isDarkMode ? '#e2e8f0' : '#334155',
                focusRingColor: isDarkMode ? '#475569' : '#cbd5e1'
              }}
              onClick={onCancel}
              disabled={isLoading}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#475569' : '#e2e8f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? '#334155' : '#f1f5f9';
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-5 py-2.5 rounded-lg font-medium text-sm focus:outline-none focus:ring-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#dc2626',
                color: '#ffffff',
                focusRingColor: '#ef4444'
              }}
              onClick={onConfirm}
              disabled={isLoading}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#b91c1c';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#dc2626';
              }}
            >
              {isLoading ? (
                <span className="inline-flex items-center">
                  <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                  </svg>
                  Deleting...
                </span>
              ) : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ConfirmationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

ConfirmationModal.defaultProps = {
  isLoading: false
};

export default ConfirmationModal;